import styles from './CardInfo.module.scss';
import { PropsWithChildren, useMemo } from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import classNames from "classnames";
import { DiamondAnimation } from '../diamond-animation';
import { motion } from 'framer-motion'
import { RarityTags } from '../inventory/card-item/rarity-tags/RarityTags';
import { INVENTORY_RARITY } from '@store';
import { CameraCardFolderMask } from '@shared';

type Props = PropsWithChildren<{
  url: string,
  effects: {
    cardUrl?: string
    mask?: boolean,
    moveDown?: boolean,
    holographic?: boolean
    fullFilling?: boolean
    diamondColor?: string
    skipRarityTag?: boolean
  },
  rarity?: number
  onClose: () => void
  title?: string
  description?: string
  className?: string
}>

export const CardInfo = (props: Props) => {
  const assetContent = useMemo(() => {
    const extension = props.url.split('.').pop()
    if (extension === 'lottie') {
      return <DotLottieReact
        src={props.url}
        autoplay
        loop
      />
    }
    if (extension === 'mp4') {
      return <video src={props.url} autoPlay loop muted playsInline className={classNames(styles.image, {
        [styles.mask]: true,
      })} />
    }
    return <img src={props.effects.cardUrl ?? props.url} className={classNames(styles.image, {
        [styles.mask]: props.effects.mask && !props.effects.cardUrl,
        [styles.cardScale]: props.effects.cardUrl,
      })}
     />
  }, [props.url, props.effects.mask])
  return (
    <motion.div
      className={styles.backdrop}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className={classNames(styles.border, 
        props.rarity ? styles[`border__rarity-veginnete-${INVENTORY_RARITY[props.rarity]}`] : '', props.className
      )}>
        <div className={styles.content}>
          <div className={styles.cardInfo}>
            <svg
              viewBox="0 0 20 20"
              fill="none"
              className={styles.closeIcon}
              onClick={props.onClose}
            >
              <line x1="3.8" y1="4.1" x2="15.9" y2="16.1" stroke="white" />
              <line x1="15.9" y1="3.9" x2="3.8" y2="15.9" stroke="white" />
            </svg>
            <div className={classNames(styles.imageWrapper, {
              [styles['imageWrapper__moveDown']]: props.effects.moveDown,
              [styles['imageWrapper__full-filling']]: props.effects.fullFilling,
              [styles['imageWrapper__holographic']]: props.effects.holographic,
            })}>
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{
                  duration: 2,
                  repeat: 1,
                  ease: 'easeInOut',
                }}
                className={styles.imageMotion}
              >
                {assetContent}
              </motion.div>
              <svg viewBox="0 0 199 183" fill="none" className={styles.diamond}>
                <DiamondAnimation optimized diamondColor={props.effects.diamondColor}/>
              </svg>
            </div>
            <div className={styles.textContainer}>
              {props.title &&<div className={styles.title}>{props.title}</div>}
              {!props.effects.skipRarityTag && props.rarity ? <RarityTags rarity={props.rarity} className={styles.rarityTags} /> : null}
              {props.description && <div className={styles.description}>{props.description}</div>}
            </div>
          </div>
          {props.children}
        </div>
      </div></motion.div>
  );
};